import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  getTimesheets,
  createTimesheet,
  updateTimesheet,
  approveTimesheet,
  rejectTimesheet,
  deleteTimesheet,
  getTimesheetsByEmployee,
  bulkApproveTimesheets,
} from '../services/timesheetService';
import { employeeService } from '../services/api';
import TimesheetForm from './TimesheetForm';
import TimesheetList from './TimesheetList';
import TimesheetCalendar from './TimesheetCalendar';
import MonthlySummary from './MonthlySummary';
import AbsenceReport from './AbsenceReport';
import OvertimeReport from './OvertimeReport';
import BulkTimesheetEditor from './BulkTimesheetEditor';
import './EmployeeManagement.css';

const TimesheetManagement = () => {
  const { hasModulePermission } = useAuth();
  const [timesheets, setTimesheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [selected, setSelected] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isBulkEditorOpen, setIsBulkEditorOpen] = useState(false);
  const [bulkEditorDate, setBulkEditorDate] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('list');
  
  // Nouveaux états pour les filtres
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [employeeSearchQuery, setEmployeeSearchQuery] = useState('');
  const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const canApprove = hasModulePermission('TIMESHEETS', 'approve');
  const canDelete = hasModulePermission('TIMESHEETS', 'delete');
  const canCreate = hasModulePermission('TIMESHEETS', 'create');
  const canUpdate = hasModulePermission('TIMESHEETS', 'update');

  const load = useCallback(async () => {
    try {
      setError('');
      const params = {};
      if (selectedEmployeeId) {
        params.employeeId = Number(selectedEmployeeId);
      }
      if (startDate) {
        params.startDate = startDate;
      }
      if (endDate) {
        params.endDate = endDate;
      }
      
      const res = await getTimesheets(params);
      let data = res.data || [];
      // Trier par date décroissante (plus récent en premier)
      data.sort((a, b) => {
        const dateA = a.date ? new Date(a.date) : new Date(0);
        const dateB = b.date ? new Date(b.date) : new Date(0);
        return dateB - dateA; // Décroissant
      });
      setTimesheets(data);
      setPage(1);
    } catch (e) {
      console.error('Erreur lors du chargement des pointages:', e);
      setError('Erreur lors du chargement des pointages: ' + (e.response?.data?.message || e.message));
    }
  }, [selectedEmployeeId, startDate, endDate]);

  const loadEmployees = async () => {
    try {
      const res = await employeeService.getAll();
      setEmployees(res.data || []);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(timesheets.length / pageSize));
  const displayedTimesheets = timesheets.slice((page - 1) * pageSize, page * pageSize);
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setPage(newPage);
  };

  const handleSubmit = async (data) => {
    try {
      setError('');
      if (selected) {
        await updateTimesheet(selected.id, data);
      } else {
        await createTimesheet(data);
      }
      setIsFormOpen(false);
      setSelected(null);
      load();
    } catch (e) {
      const errorMessage = e.response?.data?.message || e.response?.data?.error || e.message || 'Erreur lors de l\'enregistrement du pointage';
      setError(errorMessage);
      // Ne pas lancer l'exception pour éviter l'écran noir
      // L'erreur est déjà affichée via setError
    }
  };

  const handleApprove = async (ts) => {
    try {
      await approveTimesheet(ts.id);
      load();
    } catch {
      setError('Erreur lors de l’approbation');
    }
  };

  const handleBulkApprovePending = async () => {
    try {
      setError('');
      const pendingIds = timesheets.filter((ts) => ts.status === 'PENDING').map((ts) => ts.id);
      if (!pendingIds.length) {
        setError('Aucun pointage en attente à valider (en fonction des filtres actuels).');
        return;
      }
      const confirmed = window.confirm(
        `Valider tous les ${pendingIds.length} pointages en statut EN ATTENTE affichés avec les filtres actuels ?`
      );
      if (!confirmed) return;
      await bulkApproveTimesheets(pendingIds);
      load();
    } catch (e) {
      console.error('Erreur lors de la validation en masse des pointages:', e);
      setError(
        e.response?.data?.message ||
          e.response?.data?.error ||
          'Erreur lors de la validation en masse des pointages'
      );
    }
  };

  const handleReject = async (ts) => {
    const reason = window.prompt('Raison du rejet ?');
    try {
      await rejectTimesheet(ts.id, reason || '');
      load();
    } catch {
      setError('Erreur lors du rejet');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce pointage ?')) return;
    try {
      await deleteTimesheet(id);
      load();
    } catch {
      setError('Erreur lors de la suppression');
    }
  };

  const checkConflict = async (employeeId, date) => {
    if (!employeeId || !date) return '';
    // Chargement rapide du pointage existant pour cette date (si besoin)
    const res = await getTimesheetsByEmployee(employeeId, date, date);
    if (res.data && res.data.length > 0) {
      return 'Un pointage existe déjà à cette date.';
    }
    return '';
  };

  const buildExportRows = () =>
    (timesheets || []).map((ts) => ({
      Date: ts.formattedDate || ts.date,
      Employé: ts.employeeName || '',
      Entrée: ts.formattedCheckInTime || ts.checkInTime || '-',
      Sortie: ts.formattedCheckOutTime || ts.checkOutTime || '-',
      'Pause (min)': ts.breakDurationMinutes ?? 0,
      Heures: ts.formattedWorkedHours || ts.workedHours || '-',
      'Heures sup': ts.formattedOvertimeHours || ts.overtimeHours || '-',
      Statut: ts.statusLabel || ts.status || '',
      Notes: ts.notes || '',
    }));

  const ensureExportable = () => {
    if (!timesheets.length) {
      setError('Aucun pointage à exporter.');
      return false;
    }
    return true;
  };

  const handleExportPdf = () => {
    if (!ensureExportable()) return;
    const rows = buildExportRows();
    const doc = new jsPDF();
    doc.text('Pointages', 14, 15);
    autoTable(doc, {
      startY: 20,
      head: [['Date', 'Employé', 'Entrée', 'Sortie', 'Pause (min)', 'Heures', 'Heures sup', 'Statut']],
      body: rows.map((r) => [r.Date, r.Employé, r.Entrée, r.Sortie, r['Pause (min)'], r.Heures, r['Heures sup'], r.Statut]),
    });
    doc.save('pointages.pdf');
  };

  const handleExportXlsx = () => {
    if (!ensureExportable()) return;
    const rows = buildExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Pointages');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, 'pointages.xlsx');
  };

  // Filtrer les employés pour la recherche
  const filteredEmployees = employees.filter(emp => {
    if (!employeeSearchQuery) return true;
    const query = employeeSearchQuery.toLowerCase();
    const fullName = `${emp.nom || ''} ${emp.prenom || ''}`.toLowerCase();
    const matricule = (emp.matricule || '').toLowerCase();
    const cin = (emp.cin || '').toLowerCase();
    const cnss = (emp.cnssNumber || '').toLowerCase();
    return fullName.includes(query) || matricule.includes(query) || cin.includes(query) || cnss.includes(query);
  });

  const handleEmployeeSelect = (employee) => {
    if (employee) {
      setSelectedEmployeeId(employee.id);
      setEmployeeSearchQuery(`${employee.nom} ${employee.prenom}`);
    } else {
      setSelectedEmployeeId(null);
      setEmployeeSearchQuery('');
    }
    setShowEmployeeDropdown(false);
  };

  const clearFilters = () => {
    setSelectedEmployeeId(null);
    setEmployeeSearchQuery('');
    setStartDate('');
    setEndDate('');
  };

  const hasActiveFilters = selectedEmployeeId || startDate || endDate;

  return (
    <div className="employee-management">
      {error && (
        <div className="error-banner">
          <span>{error}</span>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      <div className="management-header">
        <div className="tabs">
          <button className={activeTab === 'list' ? 'active' : ''} onClick={() => setActiveTab('list')}>Liste</button>
          <button className={activeTab === 'calendar' ? 'active' : ''} onClick={() => setActiveTab('calendar')}>Calendrier</button>
          <button className={activeTab === 'summary' ? 'active' : ''} onClick={() => setActiveTab('summary')}>Résumé</button>
          <button className={activeTab === 'absences' ? 'active' : ''} onClick={() => setActiveTab('absences')}>Absences</button>
          <button className={activeTab === 'overtime' ? 'active' : ''} onClick={() => setActiveTab('overtime')}>Heures</button>
        </div>
        <div className="actions" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {canCreate && (
            <>
              <button className="btn btn-primary" onClick={() => { setSelected(null); setIsFormOpen(true); }}>
                + Nouveau pointage
              </button>
              <button 
                className="btn btn-primary" 
                onClick={() => {
                  const today = new Date().toISOString().split('T')[0];
                  setBulkEditorDate(today);
                  setIsBulkEditorOpen(true);
                }}
              >
                📋 Pointage en masse
              </button>
            </>
          )}
          {activeTab === 'list' && (
            <>
              <button className="btn" onClick={handleExportPdf}>Exporter PDF</button>
              <button className="btn" onClick={handleExportXlsx}>Exporter XLSX</button>
              {canApprove && (
                <button className="btn btn-primary" onClick={handleBulkApprovePending}>
                  Valider tous les pointages en attente
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Nouvelle section de filtres */}
      {(activeTab === 'list' || activeTab === 'calendar') && (
        <div className="filters-section">
          <div className="filters-row">
            <div className="filter-group employee-filter">
              <label>Employé</label>
              <div className="employee-select-wrapper">
                <input
                  type="text"
                  className="employee-search-input"
                  placeholder="Rechercher un employé..."
                  value={employeeSearchQuery}
                  onChange={(e) => {
                    setEmployeeSearchQuery(e.target.value);
                    setShowEmployeeDropdown(true);
                    // Si on efface le texte, effacer aussi la sélection
                    if (!e.target.value) {
                      setSelectedEmployeeId(null);
                    }
                  }}
                  onFocus={() => setShowEmployeeDropdown(true)}
                  onBlur={(e) => {
                    // Ne pas fermer si on clique sur un élément du dropdown
                    if (!e.currentTarget.contains(e.relatedTarget)) {
                      setTimeout(() => setShowEmployeeDropdown(false), 200);
                    }
                  }}
                />
                {selectedEmployeeId && (
                  <button 
                    className="clear-employee-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleEmployeeSelect(null);
                    }}
                    title="Effacer"
                  >
                    ×
                  </button>
                )}
                {showEmployeeDropdown && filteredEmployees.length > 0 && (
                  <div 
                    className="employee-dropdown"
                    onMouseDown={(e) => {
                      // Empêcher le onBlur de se déclencher quand on clique dans le dropdown
                      e.preventDefault();
                    }}
                  >
                    {filteredEmployees.slice(0, 10).map(emp => (
                      <div
                        key={emp.id}
                        className={`dropdown-item ${selectedEmployeeId === emp.id ? 'selected' : ''}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleEmployeeSelect(emp);
                        }}
                      >
                        <div className="employee-option">
                          <span className="employee-name">{emp.nom} {emp.prenom}</span>
                          {emp.matricule && <span className="employee-matricule">{emp.matricule}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="filter-group">
              <label>Date début</label>
              <input
                type="date"
                className="date-input"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                }}
              />
            </div>

            <div className="filter-group">
              <label>Date fin</label>
              <input
                type="date"
                className="date-input"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                }}
              />
            </div>

            {hasActiveFilters && (
              <div className="filter-group">
                <button className="btn btn-secondary clear-filters-btn" onClick={clearFilters}>
                  Effacer les filtres
                </button>
              </div>
            )}
          </div>

          {hasActiveFilters && (
            <div className="active-filters">
              <span className="filters-label">Filtres actifs :</span>
              {selectedEmployeeId && (
                <span className="filter-badge">
                  {employees.find(e => e.id === selectedEmployeeId)?.nom} {employees.find(e => e.id === selectedEmployeeId)?.prenom}
                  <button onClick={() => handleEmployeeSelect(null)}>×</button>
                </span>
              )}
              {startDate && (
                <span className="filter-badge">
                  Du {new Date(startDate).toLocaleDateString('fr-FR')}
                  <button onClick={() => setStartDate('')}>×</button>
                </span>
              )}
              {endDate && (
                <span className="filter-badge">
                  Au {new Date(endDate).toLocaleDateString('fr-FR')}
                  <button onClick={() => setEndDate('')}>×</button>
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'list' && (
        <TimesheetList
          timesheets={displayedTimesheets}
          onEdit={(ts) => { setSelected(ts); setIsFormOpen(true); }}
          onApprove={handleApprove}
          onReject={handleReject}
          onDelete={handleDelete}
          canEdit={canUpdate}
          canApprove={canApprove}
          canDelete={canDelete}
        />
      )}

      {activeTab === 'list' && timesheets.length > 0 && (
        <div className="pagination" style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button className="btn btn-secondary" disabled={page === 1} onClick={() => handlePageChange(page - 1)}>
            Précédent
          </button>
          <span>Page {page} / {totalPages}</span>
          <button className="btn btn-secondary" disabled={page === totalPages} onClick={() => handlePageChange(page + 1)}>
            Suivant
          </button>
        </div>
      )}

      {activeTab === 'calendar' && <TimesheetCalendar timesheets={timesheets} />}
      {activeTab === 'summary' && employees.length > 0 && <MonthlySummary employeeId={employees[0].id} />}
      {activeTab === 'absences' && <AbsenceReport />}
      {activeTab === 'overtime' && employees.length > 0 && <OvertimeReport employees={employees} />}

      {isFormOpen && (
        <TimesheetForm
          employees={employees}
          timesheet={selected}
          isEditing={!!selected}
          onSubmit={handleSubmit}
          onClose={() => { setIsFormOpen(false); setSelected(null); }}
          onCheckConflict={checkConflict}
        />
      )}

      {isBulkEditorOpen && (
        <div className="modal-overlay" onClick={() => setIsBulkEditorOpen(false)}>
          <div className="modal-content bulk-modal" onClick={(e) => e.stopPropagation()}>
            <BulkTimesheetEditor
              date={bulkEditorDate}
              onClose={() => {
                setIsBulkEditorOpen(false);
                setBulkEditorDate('');
              }}
              onSave={() => {
                load();
                setIsBulkEditorOpen(false);
                setBulkEditorDate('');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetManagement;

